import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';

function normalizeItem(item) {
  if (typeof item === 'string') {
    const name = item.trim();
    return name ? { resource_type: 'legacy', resource_name: name, quantity: 1 } : null;
  }
  const name = String(item?.resource_name || item?.name || '').trim();
  if (!name) return null;
  return {
    resource_type: item.resource_type || 'legacy',
    article_type_id: item.article_type_id || null,
    rack_id: item.rack_id || null,
    access_pack_id: item.access_pack_id || null,
    resource_name: name,
    quantity: Math.max(1, Number.parseInt(item.quantity, 10) || 1),
  };
}

function cleanBlocks(blocks) {
  return (blocks || []).map(block => ({
    zone: String(block?.zone || '').trim(),
    category: String(block?.category || '').trim(),
    note: String(block?.note || '').trim(),
    items: Array.isArray(block?.items) ? block.items.map(normalizeItem).filter(Boolean) : [],
  }));
}

function emptyBlock(zone = '') {
  return { zone, category: '', note: '', items: [] };
}

function resourceKey(resource) {
  if (resource.resource_type === 'article') return `article:${resource.article_type_id}`;
  if (resource.resource_type === 'rack') return `rack:${resource.rack_id}`;
  if (resource.resource_type === 'pack') return `pack:${resource.access_pack_id}`;
  return `legacy:${resource.resource_name}`;
}

const TYPE_LABELS = {
  article: 'Artículo',
  rack: 'Rack',
  pack: 'Pack',
  legacy: 'Texto de PDF',
};

export default function EventEquipmentDraft({ value, onChange, importedFromPdf = false }) {
  const [queryByBlock, setQueryByBlock] = useState({});
  const [catalog, setCatalog] = useState([]);
  const [catalogError, setCatalogError] = useState('');
  const blocks = useMemo(() => cleanBlocks(value), [value]);
  const sections = useMemo(() => {
    const byZone = new Map();
    blocks.forEach((block, index) => {
      const zone = block.zone || 'Sin sección';
      if (!byZone.has(zone)) byZone.set(zone, []);
      byZone.get(zone).push({ block, index });
    });
    return [...byZone.entries()].map(([zone, categories]) => ({ zone, categories }));
  }, [blocks]);

  useEffect(() => {
    let active = true;
    api.get('/events/equipment-catalog')
      .then(({ data }) => {
        if (active) setCatalog(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        if (active) setCatalogError(err.response?.data?.error || 'No se pudo cargar el catálogo de inventario.');
      });
    return () => { active = false; };
  }, []);

  function update(next) { onChange(cleanBlocks(next)); }

  function updateBlock(index, patch) {
    update(blocks.map((block, blockIndex) => blockIndex === index ? { ...block, ...patch } : block));
  }

  function renameSection(previousName, nextName) {
    update(blocks.map(block => (block.zone || 'Sin sección') === previousName ? { ...block, zone: nextName } : block));
  }

  function removeBlock(index) { update(blocks.filter((_, blockIndex) => blockIndex !== index)); }

  function addResource(index, resource) {
    const nextItem = normalizeItem(resource);
    if (!nextItem) return;
    const key = resourceKey(nextItem);
    const existingIndex = blocks[index].items.findIndex(item => resourceKey(item) === key);
    const items = existingIndex === -1
      ? [...blocks[index].items, nextItem]
      : blocks[index].items.map((item, itemIndex) => itemIndex === existingIndex
        ? { ...item, quantity: item.quantity + 1 }
        : item);
    updateBlock(index, { items });
    setQueryByBlock(current => ({ ...current, [index]: '' }));
  }

  function updateItem(blockIndex, itemIndex, patch) {
    const items = blocks[blockIndex].items.map((item, index) => index === itemIndex ? { ...item, ...patch } : item);
    updateBlock(blockIndex, { items });
  }

  function removeItem(blockIndex, itemIndex) {
    updateBlock(blockIndex, { items: blocks[blockIndex].items.filter((_, index) => index !== itemIndex) });
  }

  function matchesFor(index) {
    const query = String(queryByBlock[index] || '').trim().toLocaleLowerCase('es');
    if (!query) return [];
    return catalog
      .filter(resource => `${resource.resource_name} ${resource.category} ${resource.subcategory} ${TYPE_LABELS[resource.resource_type]}`
        .toLocaleLowerCase('es').includes(query))
      .slice(0, 12);
  }

  const totalResources = blocks.reduce(
    (count, block) => count + block.items.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0),
    0
  );

  return (
    <section className="event-draft-equipment" aria-label="Equipos del evento">
      <header className="event-draft-equipment-header">
        <div>
          <span className="event-draft-eyebrow">EQUIPOS DEL EVENTO</span>
          <h3>Equipo esperado</h3>
        </div>
        <span className="event-draft-count">{totalResources} recursos</span>
      </header>
      <p className="event-draft-help">
        {importedFromPdf
          ? 'Revisá la propuesta del PDF. Los renglones marcados como texto deben reemplazarse por recursos del inventario.'
          : 'Seleccioná artículos, racks o packs del inventario. Las unidades físicas se asignan después desde Equipos.'}
      </p>
      {catalogError && <div className="event-draft-catalog-error">{catalogError}</div>}

      <div className="event-draft-sections">
        {sections.map(({ zone, categories }) => (
          <article className="event-draft-section" key={zone}>
            <div className="event-draft-section-head">
              <input
                className="event-draft-section-name"
                value={zone === 'Sin sección' ? '' : zone}
                onChange={event => renameSection(zone, event.target.value)}
                placeholder="Sección o lugar"
                aria-label="Sección o lugar"
              />
              <button className="event-draft-remove" type="button" onClick={() => update(blocks.filter(block => (block.zone || 'Sin sección') !== zone))} aria-label={`Eliminar ${zone}`}>×</button>
            </div>

            {categories.map(({ block, index }) => {
              const matches = matchesFor(index);
              return (
                <div className="event-draft-category-block" key={`category-${index}`}>
                  <div className="event-draft-category-head">
                    <input
                      value={block.category}
                      onChange={event => updateBlock(index, { category: event.target.value })}
                      placeholder="Categoría operativa"
                      aria-label="Categoría operativa"
                    />
                    <span>{block.items.length} tipos</span>
                    <button type="button" onClick={() => removeBlock(index)} aria-label="Eliminar categoría">×</button>
                  </div>
                  <div className="event-draft-table-head"><span>Recurso validado</span><span>Cant.</span><span /></div>
                  <div className="event-draft-rows">
                    {block.items.map((item, itemIndex) => (
                      <div className={`event-draft-row${item.resource_type === 'legacy' ? ' is-legacy' : ''}`} key={`${resourceKey(item)}-${itemIndex}`}>
                        <span className="event-draft-resource">
                          <strong>{item.resource_name}</strong>
                          <small>{TYPE_LABELS[item.resource_type] || item.resource_type}</small>
                        </span>
                        <input
                          className="event-draft-quantity"
                          type="number"
                          min="1"
                          max="999"
                          value={item.quantity}
                          onChange={event => updateItem(index, itemIndex, { quantity: Math.max(1, Number.parseInt(event.target.value, 10) || 1) })}
                          aria-label={`Cantidad de ${item.resource_name}`}
                        />
                        <button type="button" onClick={() => removeItem(index, itemIndex)} aria-label={`Quitar ${item.resource_name}`}>×</button>
                      </div>
                    ))}
                  </div>
                  <div className="event-draft-add-item">
                    <input
                      value={queryByBlock[index] || ''}
                      onChange={event => setQueryByBlock(current => ({ ...current, [index]: event.target.value }))}
                      placeholder="Buscar artículo, rack o pack…"
                      aria-label="Buscar equipo esperado"
                      autoComplete="off"
                    />
                    {matches.length > 0 && (
                      <div className="event-draft-catalog-menu">
                        {matches.map(resource => (
                          <button key={resourceKey(resource)} type="button" onMouseDown={event => {
                            event.preventDefault();
                            addResource(index, resource);
                          }}>
                            <span><strong>{resource.resource_name}</strong><small>{resource.category} · {TYPE_LABELS[resource.resource_type]}</small></span>
                            <em>{resource.unit_count} u.</em>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <textarea
                    value={block.note}
                    onChange={event => updateBlock(index, { note: event.target.value })}
                    placeholder="Nota operativa (opcional)"
                    rows="2"
                    aria-label="Nota operativa"
                  />
                </div>
              );
            })}
            <button className="event-draft-add-category" type="button" onClick={() => update([...blocks, emptyBlock(zone === 'Sin sección' ? '' : zone)])}>+ Nueva categoría</button>
          </article>
        ))}
      </div>

      <button className="event-draft-add-block" type="button" onClick={() => update([...blocks, emptyBlock()])}>+ Agregar sección / sub-lugar</button>
    </section>
  );
}
